import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function select(key: string) {
    const cat = categories.find((c) => c.key === key);
    if (!cat) return;
    setSelected(key);
    setOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  }

  return (
    <section id="categories" className="bg-white">
      <div className="mx-auto w-full max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-2xl font-bold text-stone-900 md:text-3xl">
            {t('categories.title')}
          </h2>
          <p className="mt-3 text-sm text-stone-500 md:text-base">
            {t('categories.subtitle')}
          </p>
        </div>

        {/* Dropdown selector */}
        <div className="mx-auto mt-8 max-w-md">
          <div ref={ref} className="relative">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex w-full items-center justify-between rounded-md border border-stone-300 bg-white px-4 py-3 text-left text-sm font-medium text-stone-800 transition-colors hover:border-emerald-400 cursor-pointer"
            >
              <span className="flex items-center gap-2 whitespace-nowrap">
                <span className="flex h-5 w-5 items-center justify-center text-base text-emerald-600">
                  <i className="ri-menu-2-line"></i>
                </span>
                <span className="text-sm">
                  {selected ? t(`categories.${selected}`) : t('categories.placeholder')}
                </span>
              </span>
              <span className="flex h-5 w-5 items-center justify-center text-base text-stone-400">
                <i className={open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
              </span>
            </button>

            {open && (
              <div className="absolute left-0 right-0 z-30 mt-2 max-h-80 overflow-y-auto rounded-lg border border-stone-200 bg-white shadow-lg">
                <div className="py-1">
                  {categories.map((cat) => (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => select(cat.key)}
                      className="flex w-full items-center justify-between whitespace-nowrap px-4 py-2.5 text-left text-sm text-stone-700 transition-colors hover:bg-emerald-50 cursor-pointer"
                    >
                      <span className="flex items-center gap-2">
                        <span className="flex h-5 w-5 items-center justify-center text-base text-stone-400">
                          <i className={cat.icon}></i>
                        </span>
                        <span className="text-sm">{t(`categories.${cat.key}`)}</span>
                      </span>
                      <span className="flex h-4 w-4 items-center justify-center text-sm text-stone-300">
                        <i className="ri-arrow-right-up-line"></i>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Category grid with images */}
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {categories.map((cat) => (
            <a
              key={cat.key}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group overflow-hidden rounded-lg border border-stone-200 bg-white transition-all hover:border-emerald-300 hover:bg-emerald-50"
            >
              <div className="relative h-32 overflow-hidden sm:h-36">
                <img
                  src={cat.image}
                  alt={cat.imageAlt}
                  title={`${t(`categories.${cat.key}`)} — USFans`}
                  className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="flex items-center justify-between gap-2 px-3 py-3">
                <span className="min-w-0 truncate text-sm font-semibold text-stone-800">
                  {t(`categories.${cat.key}`)}
                </span>
                <span className="flex h-5 w-5 shrink-0 items-center justify-center text-sm text-stone-300 transition-colors group-hover:text-emerald-600">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}